import { Trash2, CheckCircle, FileText, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Button from './Button';
import clsx from 'clsx';

export default function CEUCard({ ceu, onEdit, onDelete, userProfile, showCycleIndicator = false }) {
  const navigate = useNavigate();
  const certifyingBody = userProfile?.certifyingBody || 'RID';
  const hasBothCertifications = userProfile?.hasBothCertifications || false;
  const showRIDStatus = certifyingBody === 'RID' || hasBothCertifications;
  const showBEIStatus = certifyingBody === 'BEI' || hasBothCertifications;

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const handleCardClick = (e) => {
    // Don't navigate if clicking on buttons
    if (e.target.closest('button')) return;
    navigate(`/view-ceu/${ceu.ceuId || ceu.id}`);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigate(`/view-ceu/${ceu.ceuId || ceu.id}`);
    }
  };

  return (
    <div 
      role="button"
      tabIndex={0}
      aria-label={`View details for ${ceu.title}, ${ceu.ceuHours} CEU${ceu.ceuHours !== 1 ? 's' : ''}, completed on ${formatDate(ceu.date)}`}
      className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-md hover:border-primary/30 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 cursor-pointer group"
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
    >
      {/* Header */}
      <div className="bg-primary/5 p-4 border-b border-gray-100">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-gray-900 mb-2 leading-tight flex-1">
            {ceu.title}
          </h3>
          <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-primary transition-colors flex-shrink-0 mt-0.5" />
        </div>
        
        <div className="flex items-center gap-3 flex-wrap text-sm">
          <span className="font-semibold text-primary">
            {ceu.ceuHours} CEU{ceu.ceuHours !== 1 ? 's' : ''}
          </span>
          <span className="text-gray-600 text-xs">
            {formatDate(ceu.date)}
          </span>
          
          {ceu.certificateUrl && (
            <div className="flex items-center gap-1 bg-success/10 text-success-dark px-2 py-0.5 rounded border border-success/20">
              <FileText className="w-3 h-3" />
              <span className="text-xs font-medium">Certificate</span>
            </div>
          )}
          
          {showCycleIndicator && ceu.cyclePeriod && (
            <span className={clsx(
              'text-xs px-2 py-0.5 rounded border',
              ceu.isCurrentCycle 
                ? 'border-primary text-primary bg-primary/10' 
                : 'border-gray-300 text-gray-600'
            )}>
              {ceu.cyclePeriod}
            </span>
          )}
          
          {ceu.instructor && (
            <span className="text-gray-600 text-xs">{ceu.instructor}</span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Categories */}
        <div className="flex flex-wrap gap-1 mb-3">
          <span className="text-xs px-2 py-0.5 rounded border border-gray-300 text-gray-700">
            {ceu.category}
          </span>
          {ceu.subcategories?.slice(0, 2).map((sub, index) => (
            <span key={index} className="text-xs px-2 py-0.5 rounded border border-gray-300 text-gray-600 opacity-70">
              {sub}
            </span>
          ))}
          {ceu.subcategories?.length > 2 && (
            <span className="text-xs px-2 py-0.5 rounded border border-gray-300 text-gray-500 opacity-50">
              +{ceu.subcategories.length - 2}
            </span>
          )}
        </div>

        {/* Description */}
        {ceu.description && (
          <p className="text-xs text-gray-600 leading-snug mb-3 overflow-hidden" style={{ 
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}>
            {ceu.description}
          </p>
        )}

        {/* Processing Status */}
        {(showRIDStatus || showBEIStatus) && (
          <div className="flex gap-3 flex-wrap mb-3">
            {showRIDStatus && (
              <div className="flex items-center gap-1">
                {ceu.ridReceived ? (
                  <CheckCircle className="w-3 h-3 text-success" />
                ) : (
                  <div className="w-3 h-3 rounded-full border border-warning" />
                )}
                <span className="text-xs text-gray-600">
                  RID: {ceu.ridReceived ? 'Received' : 'Pending'}
                </span>
              </div>
            )}
            
            {showBEIStatus && (
              <div className="flex items-center gap-1">
                {ceu.beiReceived ? (
                  <CheckCircle className="w-3 h-3 text-success" />
                ) : (
                  <div className="w-3 h-3 rounded-full border border-warning" />
                )}
                <span className="text-xs text-gray-600">
                  BEI: {ceu.beiReceived ? 'Received' : 'Pending'}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end pt-2 border-t border-gray-100">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(ceu)}
            className="text-error hover:bg-error/5"
            aria-label={`Delete ${ceu.title}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
